/**
 * Year6TeamPicker — pre-upload team selection for /e/:slug Year 6 events.
 *
 * No team = no identity = lower engagement. This modal forces a quick
 * 4-tap team choice the first time a student lands on the page. After
 * picking, the team_id is stored in localStorage AND posted to the
 * server so leaderboard scores attribute correctly.
 */
import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Check } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TEAMS = [
  { id: 'blue',   name: 'Blue',   color: '#3B82F6', emoji: '🔵' },
  { id: 'red',    name: 'Red',    color: '#EF4444', emoji: '🔴' },
  { id: 'green',  name: 'Green',  color: '#22C55E', emoji: '🟢' },
  { id: 'yellow', name: 'Yellow', color: '#FACC15', emoji: '🟡' },
];

const lsKey = (eid) => `year6_team_${eid}`;

const getAnonId = () => {
  try {
    let id = localStorage.getItem('anon_id');
    if (!id) { id = 'a_' + Math.random().toString(36).slice(2, 14); localStorage.setItem('anon_id', id); }
    return id;
  } catch { return null; }
};

export default function Year6TeamPicker({ event }) {
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!event?.event_id || event.type !== 'year6') return;
    const cached = (() => { try { return localStorage.getItem(lsKey(event.event_id)); } catch { return null; } })();
    if (cached) { setPicked(cached); return; }

    // Verify against server (in case student cleared localStorage)
    const anon = getAnonId();
    if (!anon) { setOpen(true); return; }
    axios.get(`${API}/events/${event.event_id}/year6/my-team?anon_id=${anon}`)
      .then((r) => {
        if (r.data?.team_id) {
          setPicked(r.data.team_id);
          try { localStorage.setItem(lsKey(event.event_id), r.data.team_id); } catch { /* */ }
        } else {
          setOpen(true);
        }
      })
      .catch(() => setOpen(true));
  }, [event?.event_id, event?.type]);

  const choose = async (team_id) => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/events/${event.event_id}/year6/join-team`, {
        anon_id: getAnonId(), team_id,
      });
      try { localStorage.setItem(lsKey(event.event_id), team_id); } catch { /* */ }
      setPicked(team_id);
      // Brief celebratory pause so the check animation lands
      setTimeout(() => setOpen(false), 700);
    } catch {
      setSubmitting(false);
    }
  };

  if (event?.type !== 'year6') return null;
  if (!open) return null;

  const pickedTeam = TEAMS.find((t) => t.id === picked);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center bg-black/85 backdrop-blur-md p-0 sm:p-4"
        data-testid="year6-team-picker-backdrop"
      >
        <motion.div
          initial={{ y: 40, opacity: 0, scale: 0.96 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: 20, opacity: 0 }}
          transition={{ duration: 0.25, ease: 'easeOut' }}
          className="relative w-full sm:max-w-md bg-[#0F1014] border border-white/10 rounded-t-3xl sm:rounded-3xl p-7 text-white shadow-2xl"
          data-testid="year6-team-picker"
        >
          <div className="text-center mb-7">
            <p className="text-[10px] font-black uppercase tracking-[0.22em] text-amber-300 mb-2">🎓 Year 6 Leavers</p>
            <h2 className="font-serif text-3xl font-black mb-2">Pick your team</h2>
            <p className="text-white/55 text-sm">Earn points for your house every time you upload.</p>
          </div>

          <div className="grid grid-cols-2 gap-3" data-testid="year6-team-picker-grid">
            {TEAMS.map((t) => (
              <button
                key={t.id}
                onClick={() => !submitting && choose(t.id)}
                disabled={submitting}
                className={`relative rounded-2xl p-5 border-2 transition-all active:scale-[0.97] disabled:cursor-not-allowed ${picked === t.id ? 'border-white' : 'border-white/15 hover:border-white/30'}`}
                style={{ background: `linear-gradient(135deg, ${t.color}28, ${t.color}10)` }}
                data-testid={`year6-team-pick-${t.id}`}
              >
                <div className="flex flex-col items-center gap-2">
                  <span className="w-12 h-12 rounded-full flex items-center justify-center text-2xl" style={{ background: t.color }}>
                    {picked === t.id ? <Check size={26} weight="bold" className="text-white" /> : <span>{t.emoji}</span>}
                  </span>
                  <span className="text-base font-black">{t.name} Team</span>
                </div>
              </button>
            ))}
          </div>

          {pickedTeam && (
            <p className="text-center text-[12px] text-white/55 mt-5" data-testid="year6-team-picker-confirm">
              You're on <span className="font-bold" style={{ color: pickedTeam.color }}>{pickedTeam.name}</span>! 🎉
            </p>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
